<template>
  <div class="da-rank">
    <div class="da-rank__head">
      <h2 class="da-rank__title">
        Ranking por Atendente
      </h2>
      <p class="da-rank__sub">
        Comparação visual no período selecionado.
      </p>
      <div
        class="da-rank__cta"
        role="status"
      >
        <div class="da-rank__cta-icon" aria-hidden="true">
          <feather-icon
            icon="MousePointerIcon"
            size="20"
          />
        </div>
        <p class="da-rank__cta-text">
          <strong class="da-rank__cta-strong">Clique no nome do atendente</strong>
          para abrir o detalhamento completo — gráficos e métricas do período selecionado.
        </p>
      </div>
    </div>

    <div
      v-if="loading"
      class="da-rank__loading"
    >
      <b-spinner type="grow" />
      <span>Carregando dados do período…</span>
    </div>
    <div
      v-else-if="!rows.length"
      class="da-rank__empty"
    >
      {{ emptyText }}
    </div>
    <div
      v-else
      class="da-rank__body"
    >
      <div class="da-rank__toolbar">
        <label class="da-rank__search-label" for="da-rank-busca-atendente">Buscar atendente</label>
        <div class="da-rank__search-wrap">
          <feather-icon
            icon="SearchIcon"
            size="18"
            class="da-rank__search-ico"
          />
          <b-form-input
            id="da-rank-busca-atendente"
            v-model="buscaNome"
            type="search"
            autocomplete="off"
            placeholder="Digite o nome para filtrar o ranking…"
            class="da-rank__search"
          />
        </div>
        <p
          v-if="rows.length && totalFiltradas > 0"
          class="da-rank__toolbar-meta"
        >
          {{ totalFiltradas }} {{ totalFiltradas === 1 ? 'atendente' : 'atendentes' }}
          <span v-if="buscaNome"> · filtro ativo</span>
          <span v-if="showPagination">
            · página {{ currentPage }} de {{ totalPaginas }}
          </span>
        </p>
      </div>

      <div
        v-if="!totalFiltradas"
        class="da-rank__empty da-rank__empty--filter"
      >
        Nenhum atendente encontrado com “{{ buscaNome }}”. Tente outro termo ou limpe a busca.
      </div>

      <template v-else>
        <div class="da-rank__shell">
          <!-- Desktop / tablet header -->
          <div class="da-rank__grid da-rank__grid--head">
            <span class="da-rank__h">#</span>
            <span class="da-rank__h da-rank__h--nome">Atendente</span>
            <span class="da-rank__h">Conversas</span>
            <span class="da-rank__h">Iniciadas</span>
            <span class="da-rank__h">1ª resposta</span>
            <span class="da-rank__h">Resolução</span>
            <span class="da-rank__h">Resol.</span>
            <span class="da-rank__h">Taxa resol.</span>
          </div>

          <div
            v-for="row in rowsPagina"
            :key="row.id"
            class="da-rank__grid da-rank__row"
            :class="{
              'da-rank__row--first': row.rank === 1,
              'da-rank__row--bot': row.isBot,
            }"
          >
            <div class="da-rank__cell da-rank__cell--rank">
              <span
                class="da-rank__badge-num"
                :class="`da-rank__badge-num--t${Math.min(row.rank, 3)}`"
              >{{ row.rank }}</span>
            </div>

            <div class="da-rank__cell da-rank__cell--nome">
              <div class="da-rank__who">
                <div
                  class="da-rank__avatar"
                  :class="{ 'da-rank__avatar--bot': row.isBot }"
                >
                  <feather-icon
                    v-if="row.isBot"
                    icon="CpuIcon"
                    size="18"
                  />
                  <span v-else>{{ row.initials }}</span>
                </div>
                <div class="da-rank__who-text">
                  <router-link
                    :to="row.detalheRoute"
                    class="da-rank__link"
                  >
                    {{ row.nome }}
                  </router-link>
                  <span
                    v-if="row.isBot"
                    class="da-rank__pill-bot"
                  >BOT</span>
                </div>
              </div>
            </div>

            <div class="da-rank__cell">
              <div class="da-rank__metric-block">
                <span class="da-rank__num">{{ row.numero_conversas }}</span>
                <div class="da-rank__bar-track">
                  <div
                    class="da-rank__bar-fill da-rank__bar-fill--vol"
                    :style="{ width: `${row.pctVolume}%` }"
                  />
                </div>
              </div>
            </div>

            <div class="da-rank__cell">
              <span class="da-rank__num">{{ row.conversas_iniciadas_pelo_atendente }}</span>
            </div>

            <div class="da-rank__cell">
              <span
                class="da-rank__pill"
                :class="row.clsPrimeira"
              >{{ row.lblPrimeira }}</span>
            </div>

            <div class="da-rank__cell">
              <span
                class="da-rank__pill"
                :class="row.clsResolucao"
              >{{ row.lblResolucao }}</span>
            </div>

            <div class="da-rank__cell">
              <span class="da-rank__num">{{ row.contagem_resolucao }}</span>
            </div>

            <div class="da-rank__cell">
              <div class="da-rank__metric-block">
                <span class="da-rank__num da-rank__num--sm">{{ row.taxaResolucaoFmt }}</span>
                <div class="da-rank__bar-track">
                  <div
                    class="da-rank__bar-fill da-rank__bar-fill--taxa"
                    :style="{ width: `${row.pctTaxa}%` }"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Mobile cards -->
          <div class="da-rank__mobile">
            <article
              v-for="row in rowsPagina"
              :key="`m-${row.id}`"
              class="da-rank-mcard"
            >
              <div class="da-rank-mcard__top">
                <span
                  class="da-rank__badge-num"
                  :class="`da-rank__badge-num--t${Math.min(row.rank, 3)}`"
                >{{ row.rank }}</span>
                <div
                  class="da-rank__avatar da-rank-mcard__avatar"
                  :class="{ 'da-rank__avatar--bot': row.isBot }"
                >
                  <feather-icon
                    v-if="row.isBot"
                    icon="CpuIcon"
                    size="18"
                  />
                  <span v-else>{{ row.initials }}</span>
                </div>
                <div class="da-rank-mcard__title">
                  <router-link
                    :to="row.detalheRoute"
                    class="da-rank__link"
                  >{{ row.nome }}</router-link>
                  <span
                    v-if="row.isBot"
                    class="da-rank__pill-bot"
                  >BOT</span>
                </div>
              </div>
              <dl class="da-rank-mcard__dl">
                <div><dt>Conversas</dt><dd>{{ row.numero_conversas }}</dd></div>
                <div><dt>Iniciadas</dt><dd>{{ row.conversas_iniciadas_pelo_atendente }}</dd></div>
                <div><dt>1ª resposta</dt><dd><span class="da-rank__pill" :class="row.clsPrimeira">{{ row.lblPrimeira }}</span></dd></div>
                <div><dt>Resolução</dt><dd><span class="da-rank__pill" :class="row.clsResolucao">{{ row.lblResolucao }}</span></dd></div>
                <div><dt>Resoluções</dt><dd>{{ row.contagem_resolucao }}</dd></div>
                <div><dt>Taxa resol.</dt><dd>{{ row.taxaResolucaoFmt }}</dd></div>
              </dl>
            </article>
          </div>
        </div>

        <div
          v-if="showPagination"
          class="da-rank__pager"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="totalFiltradas"
            :per-page="perPage"
            class="da-rank__pagination mb-0"
            pills
            align="center"
            size="md"
            first-number
            last-number
            prev-text="Anterior"
            next-text="Próxima"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
const PER_PAGE = 10

export default {
  name: 'RankingAtendentes',
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: 'Nenhum dado no período selecionado.',
    },
  },
  data() {
    return {
      buscaNome: '',
      currentPage: 1,
      perPage: PER_PAGE,
    }
  },
  computed: {
    rowsFiltradas() {
      const q = (this.buscaNome || '').trim().toLowerCase()
      if (!q) return this.rows
      return this.rows.filter(r => String(r.nome || '').toLowerCase().includes(q))
    },
    totalFiltradas() {
      return this.rowsFiltradas.length
    },
    totalPaginas() {
      return Math.max(1, Math.ceil(this.totalFiltradas / this.perPage))
    },
    rowsPagina() {
      const start = (this.currentPage - 1) * this.perPage
      return this.rowsFiltradas.slice(start, start + this.perPage)
    },
    showPagination() {
      return this.totalFiltradas > this.perPage
    },
  },
  watch: {
    buscaNome() {
      this.currentPage = 1
    },
    totalPaginas(novo) {
      if (this.currentPage > novo) {
        this.currentPage = novo
      }
    },
  },
}
</script>

<style scoped>
.da-rank {
  font-family: 'Inter', system-ui, sans-serif;
}

.da-rank__head {
  margin-bottom: 1.25rem;
}

.da-rank__title {
  margin: 0 0 0.35rem;
  font-size: clamp(1.25rem, 2.5vw, 1.65rem);
  font-weight: 800;
  color: #3c315b;
  letter-spacing: -0.02em;
  font-family: 'Plus Jakarta Sans', 'Sora', system-ui, sans-serif;
}

.da-rank__sub {
  margin: 0 0 0.85rem;
  font-size: 0.9rem;
  color: #6f6787;
  line-height: 1.45;
  max-width: 720px;
}

.da-rank__cta {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  max-width: 640px;
  padding: 0.95rem 1.15rem;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(111, 53, 208, 0.1) 0%, rgba(255, 111, 97, 0.08) 100%);
  border: 1px solid rgba(111, 53, 208, 0.28);
  box-shadow: 0 6px 20px rgba(111, 53, 208, 0.1);
}

.da-rank__cta-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6f35d0 !important;
  border: 1px solid rgba(111, 53, 208, 0.15);
}

.da-rank__cta-text {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
  color: #3c315b;
  font-weight: 500;
}

.da-rank__cta-strong {
  color: #5e2bb8;
  font-weight: 800;
}

.da-rank__loading,
.da-rank__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem 1.5rem;
  color: #8c84a3;
  font-weight: 600;
}

.da-rank__empty--filter {
  padding: 2rem 1.25rem;
  text-align: center;
  background: #fbf8fe;
  border-radius: 20px;
  border: 1px dashed #e0d4ef;
  margin-bottom: 0.5rem;
  color: #6f6787;
  font-weight: 600;
  line-height: 1.45;
}

.da-rank__toolbar {
  margin-bottom: 1rem;
}

.da-rank__search-label {
  display: block;
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #7a7294;
  margin-bottom: 0.4rem;
}

.da-rank__search-wrap {
  position: relative;
  max-width: 420px;
}

.da-rank__search-ico {
  position: absolute;
  left: 0.9rem;
  top: 50%;
  transform: translateY(-50%);
  color: #8a46e8 !important;
  pointer-events: none;
  z-index: 1;
}

.da-rank__search {
  padding-left: 2.65rem !important;
  height: 48px !important;
  border-radius: 14px !important;
  border: 1px solid #e9dff5 !important;
  background: #fcfafe !important;
  font-size: 0.92rem;
  color: #3c315b;
}

.da-rank__search:focus {
  border-color: #8a46e8 !important;
  box-shadow: 0 0 0 4px rgba(138, 70, 232, 0.12) !important;
}

.da-rank__toolbar-meta {
  margin: 0.55rem 0 0;
  font-size: 0.82rem;
  font-weight: 600;
  color: #8c84a3;
}

.da-rank__shell {
  background: #fbf8fe;
  border-radius: 24px;
  border: 1px solid #e9dff5;
  box-shadow: 0 10px 30px rgba(80, 45, 120, 0.08);
  padding: 1.25rem 1rem 1.5rem;
  overflow: visible;
}

.da-rank__grid {
  display: grid;
  grid-template-columns: 44px minmax(140px, 1.3fr) minmax(100px, 0.95fr) 72px 100px 100px 64px minmax(88px, 0.85fr);
  gap: 0.5rem 0.65rem;
  align-items: center;
  padding: 0.35rem 0.5rem;
}

.da-rank__grid--head {
  padding-bottom: 0.65rem;
  margin-bottom: 0.25rem;
  border-bottom: 1px solid #e9dff5;
}

.da-rank__h {
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8c84a3;
}

.da-rank__h--nome {
  padding-left: 0.25rem;
}

.da-rank__row {
  border-radius: 16px;
  transition: background 0.2s ease, box-shadow 0.2s ease;
}

.da-rank__row:hover {
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 6px 18px rgba(80, 45, 120, 0.05);
}

.da-rank__row--first {
  background: linear-gradient(90deg, rgba(111, 53, 208, 0.07) 0%, rgba(255, 255, 255, 0.5) 100%);
}

.da-rank__row--bot {
  border: 1px solid rgba(111, 53, 208, 0.14);
}

.da-rank__cell--rank {
  justify-self: start;
}

.da-rank__badge-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 800;
  background: #ece6f5;
  color: #5e2bb8;
}

.da-rank__badge-num--t1 {
  background: linear-gradient(135deg, #6f35d0, #8a46e8);
  color: #fff;
}

.da-rank__badge-num--t2 {
  background: linear-gradient(135deg, #e8b923, #f5b547);
  color: #3c315b;
}

.da-rank__badge-num--t3 {
  background: linear-gradient(135deg, #d4a574, #c98a5c);
  color: #fff;
}

.da-rank__who {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
}

.da-rank__avatar {
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: linear-gradient(135deg, #6b8af7, #42cfc3);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  font-weight: 800;
  flex-shrink: 0;
}

.da-rank__avatar--bot {
  background: rgba(111, 53, 208, 0.18);
  color: #6f35d0;
  border: 1px solid rgba(111, 53, 208, 0.22);
}

.da-rank__who-text {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
}

.da-rank__link {
  font-weight: 700;
  color: #5e2bb8;
  text-decoration: none;
  transition: color 0.2s ease;
}

.da-rank__link:hover {
  color: #8a46e8;
  text-decoration: underline;
}

.da-rank__pill-bot {
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  background: rgba(111, 53, 208, 0.12);
  color: #6f35d0;
  border: 1px solid rgba(111, 53, 208, 0.18);
}

.da-rank__num {
  font-weight: 800;
  font-size: 0.95rem;
  color: #3c315b;
}

.da-rank__num--sm {
  font-size: 0.88rem;
}

.da-rank__metric-block {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.da-rank__bar-track {
  height: 6px;
  border-radius: 999px;
  background: #e9dff5;
  overflow: hidden;
  max-width: 100%;
}

.da-rank__bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.35s ease;
}

.da-rank__bar-fill--vol {
  background: linear-gradient(90deg, #6b8af7 0%, #42cfc3 100%);
}

.da-rank__bar-fill--taxa {
  background: linear-gradient(90deg, #f5b547 0%, #ff8a65 100%);
}

.da-rank__pill {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.28rem 0.55rem;
  border-radius: 999px;
  white-space: nowrap;
}

.da-rank__pill--ok {
  background: rgba(37, 195, 142, 0.14);
  color: #1fa677;
}

.da-rank__pill--med {
  background: rgba(245, 181, 71, 0.18);
  color: #c48a12;
}

.da-rank__pill--bad {
  background: rgba(241, 106, 106, 0.14);
  color: #d94d4d;
}

.da-rank__pill--na {
  background: #efe8f7;
  color: #8c84a3;
}

.da-rank__mobile {
  display: none;
}

.da-rank__pager {
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #e9dff5;
}

.da-rank__pagination >>> .page-item.active .page-link {
  background: linear-gradient(135deg, #6f35d0, #8a46e8);
  border-color: #6f35d0;
}

.da-rank__pagination >>> .page-link {
  color: #5e2bb8;
  border-radius: 10px;
  margin: 0 2px;
  border-color: #e9dff5;
}

@media (max-width: 1199px) {
  .da-rank__grid {
    grid-template-columns: 40px minmax(120px, 1.2fr) 80px 56px 88px 88px 52px 80px;
    font-size: 0.88rem;
  }
}

@media (max-width: 991px) {
  .da-rank__grid--head,
  .da-rank__row {
    display: none;
  }

  .da-rank__mobile {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }

  .da-rank__shell {
    padding: 1rem;
  }
}

.da-rank-mcard {
  background: #fff;
  border-radius: 20px;
  border: 1px solid #e9dff5;
  box-shadow: 0 6px 18px rgba(80, 45, 120, 0.05);
  padding: 1rem 1.05rem;
  transition: box-shadow 0.22s ease, transform 0.22s ease;
}

.da-rank-mcard:hover {
  box-shadow: 0 10px 26px rgba(80, 45, 120, 0.09);
  transform: translateY(-1px);
}

.da-rank-mcard__top {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.85rem;
}

.da-rank-mcard__avatar {
  width: 42px;
  height: 42px;
}

.da-rank-mcard__title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  flex: 1;
  min-width: 0;
}

.da-rank-mcard__dl {
  margin: 0;
  display: grid;
  gap: 0.5rem 1rem;
  grid-template-columns: 1fr 1fr;
}

.da-rank-mcard__dl dt {
  margin: 0;
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8c84a3;
}

.da-rank-mcard__dl dd {
  margin: 0.15rem 0 0;
  font-size: 0.88rem;
  font-weight: 700;
  color: #3c315b;
}
</style>
